import {
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography, CircularProgress, Alert, Box,
} from '@mui/material'
import { t } from '@lingui/macro'
import type { MissingExamsRow } from '@/core/api/statistics'
import { TRIMESTERS, getTrimesterLabel, pivotMissingExams } from '../../lib/monthlyReportHelpers'

type Props = {
  data?: MissingExamsRow[]
  isLoading: boolean
  error?: unknown
  multiEstablishment: boolean
}

export function MissingExamsTable({ data, isLoading, error, multiEstablishment }: Props) {
  if (isLoading) return <CircularProgress size={24} />
  if (error) return <Alert severity="error">{t`Error loading missing exams data`}</Alert>
  if (!data || data.length === 0) return <Typography color="text.secondary">{t`No data available`}</Typography>

  const rows = pivotMissingExams(data)

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <ExamSubTable
        title={t`Missing ultrasounds`}
        rows={rows}
        getCount={(row, tr) => row.echoCounts[tr] || 0}
        getTotal={(row) => row.totalEcho}
        multiEstablishment={multiEstablishment}
      />
      <ExamSubTable
        title={t`Missing ECGs`}
        rows={rows}
        getCount={(row, tr) => row.ecgCounts[tr] || 0}
        getTotal={(row) => row.totalECG}
        multiEstablishment={multiEstablishment}
      />
    </Box>
  )
}

type SubTableProps = {
  title: string
  rows: ReturnType<typeof pivotMissingExams>
  getCount: (row: ReturnType<typeof pivotMissingExams>[number], trimester: string) => number
  getTotal: (row: ReturnType<typeof pivotMissingExams>[number]) => number
  multiEstablishment: boolean
}

function ExamSubTable({ title, rows, getCount, getTotal, multiEstablishment }: SubTableProps) {
  const trimesterTotals: Record<string, number> = {}
  let grandTotal = 0
  for (const row of rows) {
    for (const tr of TRIMESTERS) {
      trimesterTotals[tr] = (trimesterTotals[tr] || 0) + getCount(row, tr)
    }
    grandTotal += getTotal(row)
  }

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>{title}</Typography>
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: 'warning.main' }}>
              {multiEstablishment && (
                <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>{t`Location`}</TableCell>
              )}
              {TRIMESTERS.map(tr => (
                <TableCell key={tr} align="center" sx={{ color: 'white', fontWeight: 'bold' }}>
                  {getTrimesterLabel(tr)}
                </TableCell>
              ))}
              <TableCell align="center" sx={{ color: 'white', fontWeight: 'bold' }}>{t`Total`}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map(row => (
              <TableRow key={row.establishmentName} hover>
                {multiEstablishment && (
                  <TableCell sx={{ fontWeight: 500 }}>{row.establishmentName}</TableCell>
                )}
                {TRIMESTERS.map(tr => (
                  <TableCell key={tr} align="center">{getCount(row, tr)}</TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{getTotal(row)}</TableCell>
              </TableRow>
            ))}
            {multiEstablishment && (
              <TableRow sx={{ backgroundColor: 'action.hover' }}>
                <TableCell sx={{ fontWeight: 'bold' }}>{t`Total`}</TableCell>
                {TRIMESTERS.map(tr => (
                  <TableCell key={tr} align="center" sx={{ fontWeight: 'bold' }}>{trimesterTotals[tr] || 0}</TableCell>
                ))}
                <TableCell align="center" sx={{ fontWeight: 'bold' }}>{grandTotal}</TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  )
}
